import React, { useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowRight,
    faBank,
    faCheck,
    faChevronDown,
    faCopy,
    faCreditCard,
    faIdCard,
    faInfoCircle,
    faSpinner,
    faUser
} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { useFormatCurrency } from '../../hooks/useFormatCurrency';
import { getParticipantBreakdown, sponsorFundId } from '../../utils/helpers';
import { EventUserProfile, Expense, ShareRatios, SponsorExpense } from '../../types';
import TransferQr from './TransferQr';

interface Settlement {
    from: string;
    to: string;
    amount: number;
    fromName: string;
    toName: string;
}

interface SettlementCardProps {
    settlement: Settlement;
    isSettlementPaid: boolean;
    canToggle: boolean;
    isUpdating: boolean;
    expenses: (Expense | SponsorExpense)[];
    shareRatios?: ShareRatios;
    recipientProfile: EventUserProfile | null;
    // Goes into the transfer note next to the payer's name, so the recipient can
    // tell which bill an incoming transfer settles.
    eventName?: string;
    // The sender is an external sponsorship fund rather than a participant: it has
    // no expense shares, so the usual "where your amount comes from" table would be
    // empty and the partial-transfer note would fire on every row.
    isFromSponsorFund?: boolean;
    onToggleSettlement: (settlement: Settlement, amount: number) => void;
}

const SettlementCard: React.FC<SettlementCardProps> = ({
                                                           settlement,
                                                           isSettlementPaid,
                                                           canToggle,
                                                           isUpdating,
                                                           expenses,
                                                           shareRatios,
                                                           recipientProfile,
                                                           eventName,
                                                           isFromSponsorFund = false,
                                                           onToggleSettlement
                                                       }) => {
    const { t, i18n } = useTranslation();
    const { formatCurrency } = useFormatCurrency();
    const [expanded, setExpanded] = useState(false);
    const [copied, setCopied] = useState(false);

    // Only compute the breakdown once the panel has actually been opened
    const breakdown = useMemo(
        () => (expanded && !isFromSponsorFund ? getParticipantBreakdown(expenses, settlement.from, shareRatios) : null),
        [expanded, isFromSponsorFund, expenses, settlement.from, shareRatios]
    );

    // With ratios an item is divided by shares rather than heads (÷2.5 for two
    // people and a 50% one), and the sender's own ratio multiplies their part —
    // neither is guessable from the table, so a line under it spells it out.
    const isSplitByRatio = breakdown
        ? breakdown.joined.some(item => item.splitUnits !== item.splitCount)
        : false;

    const formatUnits = (units: number) => (
        new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 2 }).format(units)
    );

    const bankInfo = recipientProfile?.bankInfo;

    // The transfer amount does not always equal the sender's whole balance:
    // simplePay adds creditor-to-creditor transfers, and sponsor groups split it up.
    const isPartial = breakdown
        ? Math.abs(Math.abs(breakdown.net) - settlement.amount) > 1
        : false;

    // The one sponsorship this fund row stands for.
    const sponsorSource = useMemo(() => {
        if (!isFromSponsorFund) return null;
        return (expenses.find(
            expense => expense.type === 'sponsor' && sponsorFundId(expense.id) === settlement.from
        ) as SponsorExpense | undefined) || null;
    }, [isFromSponsorFund, expenses, settlement.from]);

    const handleCopyAccount = async () => {
        if (!bankInfo?.accountNumber) return;
        try {
            await navigator.clipboard.writeText(bankInfo.accountNumber);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (error) {
            console.error('Error copying account number:', error);
        }
    };

    // Signed amount, so sponsorships read as money coming off what you owe
    const signedCurrency = (value: number) => (
        `${value < 0 ? '−' : ''}${formatCurrency(Math.abs(Math.round(value)))}`
    );

    return (
        <div
            className={`rounded-lg border transition-all duration-200 hover:shadow-sm ${
                isSettlementPaid
                    ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                    : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
            }`}
            role="listitem"
            aria-label={`${settlement.fromName} ${t('event.payments.status.pays')} ${formatCurrency(settlement.amount)} ${t('event.payments.status.to')} ${settlement.toName}`}
        >
            {/* Toàn bộ card bấm được để mở chi tiết. Nút đánh dấu chuyển khoản nằm
                cùng hàng với nội dung nên bảng thanh toán không cao thêm dòng nào. */}
            <div className="flex items-center">
                <button
                    type="button"
                    onClick={() => setExpanded(!expanded)}
                    aria-expanded={expanded}
                    aria-label={expanded ? t('event.payments.breakdown.hide') : t('event.payments.breakdown.show')}
                    className="flex-1 min-w-0 text-left px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 items-center">
                        {/* Người gửi */}
                        <div className="flex items-center min-w-0">
                            <div className="w-7 h-7 bg-red-100 dark:bg-red-900/30 rounded-md flex items-center justify-center mr-2 flex-shrink-0">
                                <FontAwesomeIcon icon={faUser} className="text-red-600 dark:text-red-400 text-xs" aria-hidden="true"/>
                            </div>
                            <span className="font-medium text-md text-gray-900 dark:text-white truncate">
                                {settlement.fromName}
                            </span>
                        </div>

                        {/* Người nhận */}
                        <div className="flex items-center justify-end md:order-3 min-w-0">
                            <span className="font-medium text-md text-gray-900 dark:text-white truncate text-right">
                                {settlement.toName}
                            </span>
                            <div className="w-7 h-7 bg-green-100 dark:bg-green-900/30 rounded-md flex items-center justify-center ml-2 flex-shrink-0">
                                <FontAwesomeIcon icon={faUser} className="text-green-600 dark:text-green-400 text-xs" aria-hidden="true"/>
                            </div>
                        </div>

                        {/* Số tiền */}
                        <div className="col-span-2 md:col-span-1 md:order-2 flex items-center justify-center mt-1 md:mt-0">
                            <div className="flex items-center glass-sub-panel rounded-lg px-2 py-1 w-full md:w-auto justify-center gap-1">
                                <FontAwesomeIcon icon={faArrowRight} className="text-blue-600 dark:text-blue-400 text-xs" aria-hidden="true"/>
                                <span className="font-bold text-md text-blue-700 dark:text-blue-300">
                                    {formatCurrency(settlement.amount)}
                                </span>
                                <FontAwesomeIcon
                                    icon={faChevronDown}
                                    className={`text-gray-400 text-xs transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
                                    aria-hidden="true"
                                />
                            </div>
                        </div>
                    </div>
                </button>

                {/* Chỉ người được phép mới thấy nút bấm; người khác chỉ thấy dấu đã
                    chuyển để không mất thông tin trạng thái. */}
                {canToggle ? (
                    <div className="flex-shrink-0 pl-1 pr-2">
                        <button
                            type="button"
                            onClick={() => onToggleSettlement(settlement, settlement.amount)}
                            disabled={isUpdating}
                            className={`w-8 h-8 rounded-full border-2 flex items-center justify-center transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                isSettlementPaid
                                    ? 'bg-green-500 border-green-500 text-white hover:bg-green-600'
                                    : 'border-gray-300 dark:border-gray-500 text-gray-300 dark:text-gray-500 hover:border-green-500 hover:text-green-500'
                            } ${isUpdating ? 'opacity-50 cursor-not-allowed' : ''}`}
                            title={t('event.payments.status.markAs', { status: isSettlementPaid ? t('event.payments.status.unpaid') : t('event.payments.status.paid') })}
                            aria-label={isSettlementPaid ? t('event.payments.status.markAsUnpaid') : t('event.payments.status.markAsPaid')}
                            aria-pressed={isSettlementPaid}
                        >
                            <FontAwesomeIcon
                                icon={isUpdating ? faSpinner : faCheck}
                                spin={isUpdating}
                                className="text-xs"
                                aria-hidden="true"
                            />
                        </button>
                    </div>
                ) : isSettlementPaid && (
                    <div className="flex-shrink-0 pl-1 pr-2">
                        <span
                            className="w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center"
                            title={t('event.payments.status.paid')}
                            aria-label={t('event.payments.status.paid')}
                            role="status"
                        >
                            <FontAwesomeIcon icon={faCheck} className="text-xs" aria-hidden="true"/>
                        </span>
                    </div>
                )}
            </div>

            {/* Chi tiết: số tiền ở đâu ra + chuyển cho ai bằng cách nào */}
            {expanded && (breakdown || isFromSponsorFund) && (
                <div className="px-3 pb-2 space-y-3 animate-fade-in">
                    {/* Một fund tài trợ không có share chi phí nào để giải thích */}
                    {isFromSponsorFund ? (
                        <div className="pt-2 border-t border-gray-200 dark:border-gray-600">
                            <h6 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                                {t('event.payments.sponsorFund.title')}
                            </h6>
                            <p className="text-xs text-gray-600 dark:text-gray-400">
                                {t('event.payments.sponsorFund.description', {
                                    name: settlement.fromName,
                                    amount: formatCurrency(settlement.amount),
                                    recipient: settlement.toName,
                                })}
                            </p>
                            {sponsorSource && (
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
                                    {sponsorSource.description}
                                    {' · '}
                                    {t('event.payments.sponsorFund.beneficiaries', {
                                        count: sponsorSource.splitBetween?.length || 0,
                                    })}
                                </p>
                            )}
                        </div>
                    ) : breakdown && (
                    <div className="pt-2 border-t border-gray-200 dark:border-gray-600">
                        <h6 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                            {t('event.payments.breakdown.title')}
                        </h6>

                        {breakdown.joined.length === 0 && breakdown.paid.length === 0 ? (
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                {t('event.payments.breakdown.noExpenses', { name: settlement.fromName })}
                            </p>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-xs tabular-nums">
                                    <colgroup>
                                        <col />
                                        <col className="w-24"/>
                                        <col className="w-12"/>
                                        <col className="w-28"/>
                                    </colgroup>

                                    {breakdown.joined.length > 0 && (
                                        <>
                                            <thead>
                                                <tr className="text-gray-500 dark:text-gray-400">
                                                    <th scope="col" className="text-left font-medium pb-1">
                                                        {t('event.payments.breakdown.joined', { name: settlement.fromName })}
                                                    </th>
                                                    <th scope="col" className="text-right font-medium pb-1">
                                                        {t('event.payments.breakdown.columns.total')}
                                                    </th>
                                                    <th scope="col" className="text-right font-medium pb-1">
                                                        {t('event.payments.breakdown.columns.split')}
                                                    </th>
                                                    <th scope="col" className="text-right font-medium pb-1">
                                                        {t('event.payments.breakdown.columns.share')}
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody className="border-t border-gray-200 dark:border-gray-700">
                                                {breakdown.joined.map(item => (
                                                    <tr key={item.id} className={item.isSponsor ? 'text-green-700 dark:text-green-400' : 'text-gray-700 dark:text-gray-300'}>
                                                        <td className="py-0.5 pr-2 truncate max-w-0">{item.description}</td>
                                                        <td className="py-0.5 text-right whitespace-nowrap">{signedCurrency(item.amount)}</td>
                                                        <td className="py-0.5 text-right text-gray-400 dark:text-gray-500 whitespace-nowrap">÷{formatUnits(item.splitUnits)}</td>
                                                        <td className="py-0.5 text-right font-semibold whitespace-nowrap">{signedCurrency(item.share)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </>
                                    )}

                                    {breakdown.paid.length > 0 && (
                                        <>
                                            <thead>
                                                <tr className="text-gray-500 dark:text-gray-400">
                                                    <th scope="col" colSpan={4} className="text-left font-medium pt-2 pb-1">
                                                        {t('event.payments.breakdown.paid', { name: settlement.fromName })}
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody className="border-t border-gray-200 dark:border-gray-700">
                                                {breakdown.paid.map(item => (
                                                    <tr key={item.id} className="text-gray-700 dark:text-gray-300">
                                                        <td className="py-0.5 pr-2 truncate max-w-0">{item.description}</td>
                                                        <td colSpan={3} className="py-0.5 text-right font-semibold whitespace-nowrap">
                                                            {formatCurrency(item.amount)}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </>
                                    )}

                                    <tfoot className="border-t border-dashed border-gray-300 dark:border-gray-600">
                                        <tr className="text-gray-500 dark:text-gray-400">
                                            <th scope="row" colSpan={3} className="text-left font-normal pt-1.5">
                                                {t('event.payments.breakdown.totalOwed')}
                                            </th>
                                            <td className="pt-1.5 text-right font-medium text-gray-900 dark:text-white whitespace-nowrap">
                                                {formatCurrency(Math.round(breakdown.totalOwed))}
                                            </td>
                                        </tr>
                                        <tr className="text-gray-500 dark:text-gray-400">
                                            <th scope="row" colSpan={3} className="text-left font-normal">
                                                {t('event.payments.breakdown.totalPaid')}
                                            </th>
                                            <td className="text-right font-medium text-gray-900 dark:text-white whitespace-nowrap">
                                                {formatCurrency(Math.round(breakdown.totalPaid))}
                                            </td>
                                        </tr>
                                        <tr>
                                            <th scope="row" colSpan={3} className="text-left font-semibold text-gray-600 dark:text-gray-300 pt-0.5">
                                                {t('event.payments.breakdown.net')}
                                            </th>
                                            <td className={`pt-0.5 text-right font-bold whitespace-nowrap ${
                                                breakdown.net < 0
                                                    ? 'text-red-600 dark:text-red-400'
                                                    : 'text-green-600 dark:text-green-400'
                                            }`}>
                                                {breakdown.net >= 0 ? '+' : ''}{signedCurrency(breakdown.net)}
                                            </td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        )}

                        {isSplitByRatio && (
                            <p className="flex items-start gap-1.5 text-xs text-gray-500 dark:text-gray-400 mt-1.5">
                                <FontAwesomeIcon icon={faInfoCircle} className="mt-0.5 flex-shrink-0" aria-hidden="true"/>
                                {t('event.payments.breakdown.ratioNote', {
                                    name: settlement.fromName,
                                    ratio: breakdown.shareRatio
                                })}
                            </p>
                        )}

                        {isPartial && (
                            <p className="flex items-start gap-1.5 text-xs text-gray-500 dark:text-gray-400 mt-1.5">
                                <FontAwesomeIcon icon={faInfoCircle} className="mt-0.5 flex-shrink-0" aria-hidden="true"/>
                                {t('event.payments.breakdown.partialNote', { name: settlement.fromName })}
                            </p>
                        )}
                    </div>
                    )}

                    {/* Chuyển tới ai */}
                    <div>
                        <h6 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                            {t('event.payments.transferTo.title', { name: settlement.toName })}
                        </h6>

                        {bankInfo ? (
                            <div className="glass-sub-panel rounded-lg p-2 space-y-1.5">
                                <div className="flex items-center gap-2 text-xs">
                                    <FontAwesomeIcon icon={faBank} className="text-blue-600 dark:text-blue-400 w-3.5" aria-hidden="true"/>
                                    <span className="font-medium text-gray-900 dark:text-white">{bankInfo.bankName}</span>
                                </div>
                                <div className="flex items-center gap-2 text-xs">
                                    <FontAwesomeIcon icon={faCreditCard} className="text-green-600 dark:text-green-400 w-3.5" aria-hidden="true"/>
                                    <span className="font-mono font-semibold text-gray-900 dark:text-white break-all">
                                        {bankInfo.accountNumber}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={handleCopyAccount}
                                        className="ml-auto flex items-center gap-1 px-2 py-0.5 rounded-md text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 flex-shrink-0"
                                        title={t('event.payments.transferTo.copy')}
                                        aria-label={t('event.payments.transferTo.copy')}
                                    >
                                        <FontAwesomeIcon
                                            icon={copied ? faCheck : faCopy}
                                            className={copied ? 'text-green-600 dark:text-green-400' : ''}
                                            aria-hidden="true"
                                        />
                                        {copied && <span className="text-green-600 dark:text-green-400">{t('event.payments.transferTo.copied')}</span>}
                                    </button>
                                </div>
                                <div className="flex items-center gap-2 text-xs">
                                    <FontAwesomeIcon icon={faIdCard} className="text-purple-600 dark:text-purple-400 w-3.5" aria-hidden="true"/>
                                    <span className="text-gray-900 dark:text-white">{bankInfo.accountHolder}</span>
                                </div>
                                <TransferQr
                                    bankInfo={bankInfo}
                                    amount={settlement.amount}
                                    payerName={settlement.fromName}
                                    eventName={eventName}
                                />
                            </div>
                        ) : (
                            <p className="flex items-start gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                                <FontAwesomeIcon icon={faInfoCircle} className="mt-0.5 flex-shrink-0" aria-hidden="true"/>
                                {t('event.payments.transferTo.noBankInfo', { name: settlement.toName })}
                            </p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SettlementCard;
